import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import apiService from '../services/api';

interface CodeOption {
  id: string;
  code?: string;
  description?: string;
  shortDescription?: string;
  longDescription?: string;
  fullySpecifiedName?: string;
  preferredTerm?: string;
  category?: string;
  // ICD-specific fields
  icdCode?: string;
  // SNOMED-specific fields
  snomedCode?: string;
  // CPT-specific fields
  cptCode?: string;
}

interface CodeSearchDropdownProps {
  codeType: 'icd' | 'cpt' | 'snomed';
  value: string;
  onChange: (value: string) => void;
  label: string;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

const CodeSearchDropdown: React.FC<CodeSearchDropdownProps> = ({
  codeType,
  value,
  onChange,
  label,
  disabled = false,
  multiple = false,
  placeholder,
  error = false,
  helperText,
}) => {
  const [options, setOptions] = useState<CodeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Function to get the display text for a code option
  const getOptionLabel = (option: CodeOption | string) => {
    if (typeof option === 'string') {
      return option;
    }
    
    switch (codeType) {
      case 'icd':
        return `${option.icdCode || option.code || ''} - ${option.shortDescription || option.description || ''}`;
      case 'cpt':
        return `${option.code || ''} - ${option.description || ''}`;
      case 'snomed':
        return `${option.snomedCode || option.code || ''} - ${option.fullySpecifiedName || option.preferredTerm || option.description || ''}`;
      default:
        return option.code || '';
    }
  };

  // Function to get the value for a code option
  const getOptionValue = (option: CodeOption) => {
    switch (codeType) {
      case 'icd':
        return option.icdCode || option.code || '';
      case 'cpt':
        return option.code || '';
      case 'snomed':
        return option.snomedCode || option.code || '';
      default:
        return option.code || '';
    }
  };

  // Function to fetch codes from the backend
  const fetchCodes = useCallback(async (search: string = '') => {
    setLoading(true);
    try {
      let response;
      switch (codeType) {
        case 'icd':
          response = await apiService.getIcdCodes(1, 50, search);
          setOptions(response.icdCodes || []);
          break;
        case 'cpt':
          response = await apiService.getCptCodes(1, 50, search);
          setOptions(response.cptCodes || []);
          break;
        case 'snomed':
          response = await apiService.getSnomedCodes(1, 50, search);
          setOptions(response.snomedCodes || []);
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${codeType.toUpperCase()} codes:`, error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [codeType]);

  // Load initial options when component mounts
  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  // Handle search input changes with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (inputValue !== searchTerm) {
        setSearchTerm(inputValue);
        fetchCodes(inputValue);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputValue, searchTerm, fetchCodes]);

  // Find the selected option based on the current value
  const selectedOption = options.find(option => getOptionValue(option) === value) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={selectedOption}
      onChange={(_, newValue) => {
        if (newValue) {
          if (Array.isArray(newValue)) {
            // Handle multiple selection
            onChange(newValue.map(option => getOptionValue(option)).join(', '));
          } else {
            // Handle single selection
            onChange(getOptionValue(newValue));
          }
        } else {
          onChange('');
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      loading={loading}
      disabled={disabled}
      multiple={multiple}
      filterOptions={(x) => x} // Disable client-side filtering since we're using server-side search
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {getOptionLabel(option)}
            </Typography>
            {option.category && (
              <Chip 
                label={option.category} 
                size="small" 
                sx={{ mt: 0.5, alignSelf: 'flex-start' }}
              />
            )}
          </Box>
        </Box>
      )}
      noOptionsText={loading ? 'Loading...' : 'No codes found'}
      sx={{
        '& .MuiAutocomplete-input': {
          fontSize: '0.875rem',
        },
      }}
    />
  );
};

export default CodeSearchDropdown; 